import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import type { UserRoleDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { UserTable, UserTableSensitiveColumn } from "@/components/UserTable/UserTable";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { parseQueryParamToList, parseQueryParamToString } from "@/helpers/query-params";
import { getRoleLabelByType, USER_ROLE_TYPES } from "@/helpers/user-roles";
import { getUserStatusLabelByType, USER_STATUSES, type UserStatus } from "@/helpers/user-statuses";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useUserQueries } from "@/queries/users/queryOptions";
import { useTranslation } from "@/translations";

import type { PortfolioRouteParams } from "../../routes";

type UserLookupFilterValues = {
  city?: string;
  street?: string;
  house?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  roleTypes?: UserRoleDto["type"][];
  statuses?: UserStatus[];
};

export function UsersLookupPage(): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const userQueries = useUserQueries();
  const [queryParams, setQueryParams] = useQueryParams<PortfolioRouteParams["userLookup"]>();

  const currFilter: UserLookupFilterValues = useMemo(() => {
    return {
      city: parseQueryParamToString(queryParams.city),
      street: parseQueryParamToString(queryParams.street),
      house: parseQueryParamToString(queryParams.house),
      firstName: parseQueryParamToString(queryParams.firstName),
      lastName: parseQueryParamToString(queryParams.lastName),
      email: parseQueryParamToString(queryParams.email),
      roleTypes: parseQueryParamToList(queryParams.roleTypes, (r) =>
        USER_ROLE_TYPES.includes(r as UserRoleDto["type"]),
      ),
      statuses: parseQueryParamToList(queryParams.statuses, (status) => USER_STATUSES.includes(status as any)),
    };
  }, [queryParams]);
  const isEnableDataFetching = Boolean(
    currFilter.city ||
    currFilter.street ||
    currFilter.house ||
    currFilter.firstName ||
    currFilter.lastName ||
    currFilter.email,
  );

  const {
    data: usersData,
    hasNextPage: hasMoreUsers,
    fetchNextPage: loadMoreUsers,
    isFetching: isFetchingUsers,
    isFetchingNextPage: isFetchingMoreUsers,
    error: errorUsersData,
  } = useInfiniteQuery({
    ...userQueries.lookupInfinite({
      City: currFilter.city,
      Street: currFilter.street,
      House: currFilter.house,
      FirstName: currFilter.firstName,
      LastName: currFilter.lastName,
      Email: currFilter.email,
      Statuses: currFilter.statuses,
      RoleTypes: currFilter.roleTypes ?? (sessionUser.isAdmin ? USER_ROLE_TYPES : ["resident"]),
      SortBy: "invitedAt",
    }),
    enabled: isEnableDataFetching,
  });
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreUsers && hasMoreUsers) {
        void loadMoreUsers();
      }
    }, [isFetchingMoreUsers, hasMoreUsers, loadMoreUsers]),
  });

  const formMethods = useForm<UserLookupFilterValues>({
    defaultValues: {
      city: currFilter.city,
      street: currFilter.street,
      house: currFilter.house,
      firstName: currFilter.firstName,
      lastName: currFilter.lastName,
      email: currFilter.email,
      roleTypes: currFilter.roleTypes ?? (sessionUser.isAdmin ? [...USER_ROLE_TYPES] : ["resident"]),
      statuses: currFilter.statuses || USER_STATUSES,
    },
  });

  function handleSubmit(values: UserLookupFilterValues) {
    setQueryParams({
      city: values.city,
      street: values.street,
      house: values.house,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      roleTypes: values.roleTypes ? values.roleTypes.join(",") : undefined,
      statuses: values.statuses ? values.statuses.join(",") : undefined,
    });
  }

  const users = usersData?.pages.flatMap((page) => page.items) || [];
  const amountTotalUsers = useMemo(() => usersData?.pages[0].total ?? 0, [usersData]);

  if (errorUsersData) {
    return <ErrorPage error={errorUsersData} />;
  }

  const availableRoleTypes = sessionUser.isAdmin ? USER_ROLE_TYPES : (["resident"] as const);
  const userRoleOptions = availableRoleTypes.map((type) => ({
    id: type,
    name: getRoleLabelByType(t, type),
  }));

  const userStatusOptions = USER_STATUSES.map((status) => ({
    id: status,
    name: getUserStatusLabelByType(t, status),
  }));

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.user-lookup.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.user-lookup.title"),
            },
          ]}
        />
      }
      header={
        <Form.Root formMethods={formMethods} onSubmit={handleSubmit} noPrompt>
          <Form.Container>
            <Form.Body>
              <section className="mb-space-sm grid grid-cols-1 items-center gap-space-md @sm:grid-cols-3">
                <FormField label="City">
                  <FormInput<UserLookupFilterValues> data-testid="user-lookup-city-input" name="city" />
                </FormField>
                <FormField label="Street">
                  <FormInput<UserLookupFilterValues> data-testid="user-lookup-street-input" name="street" />
                </FormField>
                <FormField label="House">
                  <FormInput<UserLookupFilterValues> data-testid="user-lookup-house-input" name="house" />
                </FormField>
                <FormField label="First Name">
                  <FormInput<UserLookupFilterValues> data-testid="user-lookup-first-name-input" name="firstName" />
                </FormField>
                <FormField label="Last Name">
                  <FormInput<UserLookupFilterValues> data-testid="user-lookup-surname-input" name="lastName" />
                </FormField>
                <FormField label="Email">
                  <FormInput<UserLookupFilterValues> data-testid="user-lookup-email-input" name="email" />
                </FormField>
                <FormField label="Status">
                  <Controller
                    name="statuses"
                    control={formMethods.control}
                    render={({ field: { onChange, value } }) => (
                      <CheckboxMultiSelect
                        renderMoreSelected={(count) => t("common.label.x-more", { count })}
                        items={userStatusOptions}
                        selected={userStatusOptions.filter((s) => value?.includes(s.id)) || []}
                        onChange={(currStatus) => onChange(currStatus.map(({ id }) => id))}
                        placeholder="Select status"
                        searchableFieldSelector={(x) => [x.name]}
                        renderOption={(x) => {
                          let variant: "success" | "danger" | "warning" = "warning";
                          if (x.id === "onboarded") {
                            variant = "success";
                          } else if (x.id === "deleted") {
                            variant = "danger";
                          }

                          return <Badge variant={variant}>{x.name}</Badge>;
                        }}
                        keySelector={(x) => x.id}
                      />
                    )}
                  />
                </FormField>
                <FormField label="Role">
                  <Controller
                    name="roleTypes"
                    control={formMethods.control}
                    render={({ field: { onChange, value } }) => (
                      <CheckboxMultiSelect
                        renderMoreSelected={(count) => t("common.label.x-more", { count })}
                        data-testid="user-lookup-role-type-select"
                        items={userRoleOptions}
                        selected={userRoleOptions.filter((r) => value?.includes(r.id))}
                        onChange={(selected) => onChange(selected.map(({ id }) => id))}
                        placeholder="Select role type"
                        searchableFieldSelector={(x) => [x.name]}
                        renderOption={(x) => x.name}
                        keySelector={(x) => x.id}
                      />
                    )}
                  />
                </FormField>
              </section>
            </Form.Body>
            <Form.Footer
              renderPrimaryAction={() => (
                <Button type="submit" isLoading={isFetchingUsers}>
                  {t("common.action.search")}
                </Button>
              )}
            />
          </Form.Container>
        </Form.Root>
      }
    >
      {isEnableDataFetching ? (
        <section className="mb-auto flex flex-col gap-space-sm" aria-live="polite">
          {(isFetchingUsers || users.length > 0) && (
            <Stack gap="sm">
              {amountTotalUsers != null && (
                <span>
                  Shows {users.length} of {amountTotalUsers} users
                </span>
              )}
              <UserTable
                data={users}
                isLoading={isFetchingUsers && !isFetchingMoreUsers}
                sensitiveColumns={[
                  UserTableSensitiveColumn.CONTACT_INFO,
                  UserTableSensitiveColumn.PROJECT,
                  UserTableSensitiveColumn.PLATFORM_DETAILS,
                ]}
              />
            </Stack>
          )}
          {!isFetchingUsers && users.length === 0 && <span>{t("common.label.no-result")}</span>}
          {hasMoreUsers && (
            <footer className="p-space-md" ref={ref}>
              <Box padding={{ y: "md" }}>
                <Inline justify="center">
                  <Spinner size="md" />
                </Inline>
              </Box>
            </footer>
          )}
        </section>
      ) : (
        <span className="mb-auto">Please fill in the form and click 'Search' to see the results.</span>
      )}
    </DocumentPage>
  );
}
